import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  COLS, STATUS_BG, STATUS_FG, ATTEND_BG, NO_OPTS,
  blankRowData, getCellVal, setCellVal, rowBg,
} from './constants';

const DATE_TABS = ['daily_work','work_problem','suggestion','company_dev','cost_cutting','senior_suggestion'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const STATUS_SORT = { INPROCESS: 0, DONE: 1, HOLD: 2, CANCEL: 3 };

const parseDateToISO = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const m = str.match(/^(\d{1,2})[.\-\/](\d{1,2})[.\-\/](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`;
  return null;
};

const getDatesInMonth = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const days = new Date(y, m, 0).getDate();
  return Array.from({length: days}, (_, i) => `${ym}-${String(i+1).padStart(2,'0')}`);
};

const SpreadsheetCell = React.memo(({ col, value, isEditing, onStartEdit, onChange, onBlur, onKeyDown, isFirst, cellBg, alwaysSel }) => {
  const inputRef = useRef(null);
  useEffect(() => { if (isEditing && inputRef.current) inputRef.current.focus(); }, [isEditing]);
  const s = (value||'').toString().toUpperCase().trim();
  const isMsPs = !!(col.msKey || col.psKey);
  const msStatusBg = isMsPs ? (STATUS_BG[s] || null) : null;
  const msStatusFg = isMsPs ? (STATUS_FG[s] || '#333') : '#333';
  const display = () => {
    if (!value && value !== 0) return <span className="text-slate-300 text-[10px]">—</span>;
    if (isMsPs && msStatusBg) return <span className="text-[11px] font-bold text-center block" style={{color:msStatusFg}}>{value}</span>;
    if (STATUS_BG[s] && !isMsPs) return <span className="px-1.5 py-0.5 rounded text-[11px] font-bold" style={{background:STATUS_BG[s],color:STATUS_FG[s]}}>{value}</span>;
    if (ATTEND_BG[s]) return <span className="px-1 py-0.5 rounded text-[11px] font-bold text-center block" style={{background:ATTEND_BG[s]}}>{value}</span>;
    return <span className="text-[11px] leading-tight">{value}</span>;
  };

  // alwaysSel removed — STATUS/NO/REMARK are now click-to-edit (cleaner UI)
  // They render as display values with ▾ only on hover (see the standard click-to-edit path below)

  return (
    <td
      style={{ minWidth: col.w, maxWidth: col.w*2, position: isFirst ? 'sticky' : 'static', left: 0, zIndex: isFirst ? 5 : 'auto', background: msStatusBg || cellBg || 'inherit', textAlign: isMsPs ? 'center' : 'left' }}
      className={`border border-slate-200 px-0 py-0 relative ${isFirst ? 'bg-slate-50 font-medium' : ''}`}
      onClick={onStartEdit}
    >
      {isEditing ? (
        col.t === 'sel' ? (
          <select ref={inputRef} value={value||''} onChange={e=>onChange(e.target.value)} onBlur={onBlur} onKeyDown={onKeyDown}
            className="w-full h-full px-1.5 py-1 text-[11px] border-0 outline-none bg-sky-50 ring-2 ring-inset ring-sky-500" style={{minWidth:col.w}}>
            {(col.o||[]).map(o=><option key={o} value={o}>{o||'—'}</option>)}
          </select>
        ) : (
          <input ref={inputRef} type={col.t==='date'||col.t==='month'?col.t:'text'} value={value||''}
            onChange={e=>onChange(col.t==='date'||col.t==='month'?e.target.value:e.target.value.toUpperCase())}
            onBlur={onBlur} onKeyDown={onKeyDown}
            className="w-full h-full px-1.5 py-1 text-[11px] border-0 outline-none bg-sky-50 ring-2 ring-inset ring-sky-500"
            style={{minWidth:col.w, textTransform: col.t==='date'||col.t==='month'?'none':'uppercase'}} />
        )
      ) : (
        col.t === 'sel' && !isMsPs ? (
          <div className="px-2 py-1 min-h-[28px] cursor-pointer flex items-center justify-between gap-1 hover:bg-blue-50 rounded-sm">
            <span>{display()}</span>
            <span className="text-slate-400 text-[9px] leading-none flex-shrink-0">▾</span>
          </div>
        ) : (
          <div className="px-2 py-1.5 min-h-[28px] cursor-cell">{display()}</div>
        )
      )}
    </td>
  );
});

const SpreadsheetTable = ({
  tab, entries, onSaveRow, onDeleteRow, onAddRow,
  personName, dept, hdrColor, hdrFg = '#fff',
  isHR = false, saveAllTrigger = 0, onDirtyCountChange,
}) => {
  const cols = COLS[tab] || COLS.daily_work;
  const [localEdits, setLocalEdits] = useState({});
  const [editingCell, setEditingCell] = useState({ rowId: null, colKey: null });
  const [bulkNo, setBulkNo] = useState('');
  const [applyingBulkNo, setApplyingBulkNo] = useState(false);
  const isIndexTab = tab === 'index1' || tab === 'index2';
  const saveTimerRef = useRef({});
  const tableContainerRef = useRef(null);
  const topScrollRef = useRef(null);
  const syncingRef = useRef(false);
  const prevTriggerRef = useRef(saveAllTrigger);

  const isDateTab = DATE_TABS.includes(tab);
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
  // Month of each entry (YYYY-MM) and counts for the month hint / auto-jump
  const entryMonths = useMemo(() => (entries || []).map(e => (parseDateToISO(e.row_data?.date || '') || '').slice(0, 7)).filter(m => /^\d{4}-\d{2}$/.test(m)), [entries]);
  const inMonthCount = entryMonths.filter(m => m === currentMonth).length;
  const otherMonthCount = entryMonths.length - inMonthCount;
  const userNavRef = useRef(false);
  useEffect(() => { userNavRef.current = false; }, [tab]);
  // When the current month has no entries (e.g. right after an Excel import of older
  // months), open on the latest month that has data instead of a blank month.
  useEffect(() => {
    if (!isDateTab || isHR || userNavRef.current || !entryMonths.length) return;
    if (entryMonths.includes(currentMonth)) return;
    const latest = [...entryMonths].sort().pop();
    if (latest && latest !== currentMonth) setCurrentMonth(latest);
  }, [entryMonths, currentMonth, isDateTab, isHR, tab]);

  const BLANK_COUNT = isDateTab ? 0 : 5;
  const blankIds = Array.from({ length: BLANK_COUNT }, (_, i) => `__new_${i}__`);

  // Top-scrollbar width update
  const [topScrollWidth, setTopScrollWidth] = useState(0);
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const update = () => setTopScrollWidth(el.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [entries, currentMonth, tab]);

  const handleTopScroll = () => {
    if (syncingRef.current || !tableContainerRef.current || !topScrollRef.current) return;
    syncingRef.current = true;
    tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    syncingRef.current = false;
  };
  const handleTableScroll = () => {
    if (syncingRef.current || !topScrollRef.current || !tableContainerRef.current) return;
    syncingRef.current = true;
    topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    syncingRef.current = false;
  };

  // Notify parent of dirty count
  useEffect(() => {
    onDirtyCountChange?.(Object.keys(localEdits).length);
  }, [localEdits]); // eslint-disable-line

  // Save all on trigger
  useEffect(() => {
    if (prevTriggerRef.current === saveAllTrigger) return;
    prevTriggerRef.current = saveAllTrigger;
    saveAllDirty();
  }, [saveAllTrigger]); // eslint-disable-line

  const blankBase = useCallback(() => blankRowData(tab, personName, dept), [tab, personName, dept]);

  // Bulk-apply NO value to all real (non-blank) rows in current Index tab
  const applyBulkNo = async (noVal) => {
    if (!noVal || applyingBulkNo) return;
    setApplyingBulkNo(true);
    for (const entry of entries) {
      if (!entry.id || entry.id.startsWith('__')) continue;
      await onSaveRow(entry.id, { ...entry.row_data, no: noVal });
    }
    setApplyingBulkNo(false);
  };

  const allDisplayRows = useMemo(() => {
    if (!isDateTab) {
      if (tab === 'index1' || tab === 'index2') {
        return [...entries].sort((a, b) => {
          const sa = (a.row_data?.status||'').toUpperCase();
          const sb = (b.row_data?.status||'').toUpperCase();
          return (STATUS_SORT[sa]??4) - (STATUS_SORT[sb]??4);
        });
      }
      return entries;
    }
    if (isHR) {
      return [...entries].sort((a, b) => {
        const da = parseDateToISO(a.row_data?.date||'') || a.row_data?.date || '';
        const db = parseDateToISO(b.row_data?.date||'') || b.row_data?.date || '';
        if (da !== db) return da.localeCompare(db);
        return (a.person_name||'').localeCompare(b.person_name||'');
      });
    }
    // Non-HR full month
    const allDates = getDatesInMonth(currentMonth);
    const existingByDate = {};
    entries.forEach(e => {
      const iso = parseDateToISO(e.row_data?.date||'') || e.row_data?.date || '';
      if (!existingByDate[iso]) existingByDate[iso] = [];
      existingByDate[iso].push(e);
    });
    const rows = [];
    allDates.forEach(dateISO => {
      if (existingByDate[dateISO]?.length) {
        existingByDate[dateISO].forEach(e => rows.push(e));
      } else {
        rows.push({ id: `__virtual_${dateISO}__`, _isVirtual: true, row_data: { ...blankBase(), date: dateISO } });
      }
    });
    // Other months entries at bottom
    const otherRows = entries.filter(e => {
      const iso = parseDateToISO(e.row_data?.date||'') || e.row_data?.date || '';
      return !allDates.includes(iso);
    });
    if (otherRows.length) {
      rows.push({ id: '__other_sep__', _isSeparator: true });
      otherRows.forEach(e => rows.push(e));
    }
    return rows;
  }, [tab, entries, isHR, currentMonth, blankBase, isDateTab]);

  const getEditVal = (rowId, col, rd) => {
    if (localEdits[rowId]?.[col.k] !== undefined) return localEdits[rowId][col.k];
    return getCellVal(rd, col);
  };

  const handleCellChange = (rowId, col, val) => {
    setLocalEdits(prev => ({...prev, [rowId]: {...(prev[rowId]||{}), [col.k]: val}}));
  };

  const startEdit = (rowId, colKey) => {
    if (saveTimerRef.current[rowId]) clearTimeout(saveTimerRef.current[rowId]);
    setEditingCell({ rowId, colKey });
  };

  const slotHasContent = (slotId) => {
    const e = localEdits[slotId];
    if (!e) return false;
    return Object.values(e).some(v => v !== '' && v != null);
  };

  const commitVirtual = (rowId, rd) => {
    const edits = localEdits[rowId] || {};
    const hasContent = Object.entries(edits).some(([k, v]) => k !== 'date' && v !== '' && v != null);
    if (!hasContent) { setLocalEdits(prev => { const n={...prev}; delete n[rowId]; return n; }); return; }
    let finalRd = { ...rd };
    Object.entries(edits).forEach(([k, v]) => {
      const c = cols.find(x => x.k === k);
      if (c) finalRd = setCellVal(finalRd, c, v);
    });
    onAddRow(finalRd);
    setLocalEdits(prev => { const n={...prev}; delete n[rowId]; return n; });
  };

  const commitBlank = (slotId) => {
    if (!slotHasContent(slotId)) { setLocalEdits(prev => { const n={...prev}; delete n[slotId]; return n; }); return; }
    let finalRd = blankBase();
    Object.entries(localEdits[slotId] || {}).forEach(([k, v]) => {
      const c = cols.find(x => x.k === k);
      if (c) finalRd = setCellVal(finalRd, c, v);
    });
    onAddRow(finalRd);
    setLocalEdits(prev => { const n={...prev}; delete n[slotId]; return n; });
  };

  const handleCellBlur = (rowId, col, isVirtual, isBlank, rd) => {
    setEditingCell({ rowId: null, colKey: null });
    if (saveTimerRef.current[rowId]) clearTimeout(saveTimerRef.current[rowId]);
    saveTimerRef.current[rowId] = setTimeout(() => {
      if (isBlank) { commitBlank(rowId); return; }
      if (isVirtual) { commitVirtual(rowId, rd); return; }
      if (!localEdits[rowId]) return;
      const entry = entries.find(e => e.id === rowId);
      if (!entry) return;
      let updated = {...entry.row_data};
      Object.entries(localEdits[rowId] || {}).forEach(([k, v]) => {
        const c = cols.find(x => x.k === k);
        if (c) updated = setCellVal(updated, c, v);
      });
      onSaveRow(rowId, updated);
      setLocalEdits(prev => { const n={...prev}; delete n[rowId]; return n; });
    }, 300);
  };

  const saveAllDirty = async () => {
    Object.values(saveTimerRef.current).forEach(t => clearTimeout(t));
    saveTimerRef.current = {};
    setEditingCell({ rowId: null, colKey: null });
    const editsSnapshot = { ...localEdits };
    setLocalEdits({});
    for (const [rowId, edits] of Object.entries(editsSnapshot)) {
      if (rowId.startsWith('__new_')) {
        const hasContent = Object.values(edits).some(v => v !== '' && v != null);
        if (!hasContent) continue;
        let finalRd = blankBase();
        Object.entries(edits).forEach(([k, v]) => {
          const c = cols.find(x => x.k === k);
          if (c) finalRd = setCellVal(finalRd, c, v);
        });
        onAddRow(finalRd);
        continue;
      }
      if (rowId.startsWith('__virtual_')) {
        const virtRow = allDisplayRows.find(r => r.id === rowId);
        if (!virtRow) continue;
        const hasContent = Object.entries(edits).some(([k, v]) => k !== 'date' && v !== '' && v != null);
        if (!hasContent) continue;
        let finalRd = { ...virtRow.row_data };
        Object.entries(edits).forEach(([k, v]) => {
          const c = cols.find(x => x.k === k);
          if (c) finalRd = setCellVal(finalRd, c, v);
        });
        onAddRow(finalRd);
        continue;
      }
      const entry = entries.find(e => e.id === rowId);
      if (!entry) continue;
      let updated = {...entry.row_data};
      Object.entries(edits).forEach(([k, v]) => {
        const c = cols.find(x => x.k === k);
        if (c) updated = setCellVal(updated, c, v);
      });
      await onSaveRow(rowId, updated);
    }
  };

  const handleKeyDown = (e, rowId, colIdx) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const nextColIdx = colIdx + 1;
      if (nextColIdx < cols.length) startEdit(rowId, cols[nextColIdx].k);
      else setEditingCell({ rowId: null, colKey: null });
    }
    if (e.key === 'Escape') setEditingCell({ rowId: null, colKey: null });
    if (e.key === 'Enter') { e.preventDefault(); setEditingCell({ rowId: null, colKey: null }); }
  };

  const totalRow = () => {
    if (!entries.length) return null;
    const done = entries.filter(e=>(e.row_data?.status||'').toUpperCase()==='DONE').length;
    const inp = entries.filter(e=>(e.row_data?.status||'').toUpperCase()==='INPROCESS').length;
    const cancel = entries.filter(e=>(e.row_data?.status||'').toUpperCase()==='CANCEL').length;
    const hold = entries.filter(e=>(e.row_data?.status||'').toUpperCase()==='HOLD').length;
    return `Total: ${entries.length} rows | In-Process: ${inp} | Done: ${done} | Cancel: ${cancel} | Hold: ${hold}`;
  };

  const prevMonth = () => {
    userNavRef.current = true;
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m-2, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };
  const nextMonth = () => {
    userNavRef.current = true;
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };
  const [ymY, ymM] = currentMonth.split('-').map(Number);
  const monthLabel = `${MONTH_NAMES[ymM-1]} ${ymY}`;

  const renderDataRow = (row, srNo = 0) => {
    const isVirtual = !!row._isVirtual;
    const rowId = row.id;
    const rd = row.row_data || {};
    const bg = isVirtual ? '#FAFBFF' : (rowBg(rd) || '#fff');
    const hasDirty = !!localEdits[rowId];
    // isIndexTab is defined at component level
    return (
      <tr key={rowId} style={{background: hasDirty ? '#FFF9E6' : bg}}>
        {/* Sr. No. display cell */}
        <td className="border border-slate-200 px-1 py-1 text-center text-[10px] font-semibold text-slate-400 bg-slate-50/60 select-none" style={{minWidth:36,maxWidth:36}}>
          {isVirtual ? <span className="text-slate-200">—</span> : srNo}
        </td>
        {cols.map((col, colIdx) => {
          const val = getEditVal(rowId, col, rd);
          const isEditing = editingCell.rowId === rowId && editingCell.colKey === col.k;
          // Index 1 & 2: force white bg for static cols, STATUS col gets its own color, month cols keep status color
          const statusVal = (rd.status || '').toUpperCase().trim();
          const cellBg = isIndexTab && !col.msKey && !col.psKey
            ? (col.k === 'status' && STATUS_BG[statusVal] ? STATUS_BG[statusVal] : '#ffffff')
            : undefined;
          // alwaysSel no longer used — removed per-row STATUS/NO/REMARK dropdowns
          const alwaysSel = false;
          return (
            <SpreadsheetCell
              key={col.k} col={col} value={val} isEditing={isEditing}
              isFirst={colIdx===0}
              cellBg={cellBg}
              alwaysSel={alwaysSel}
              onStartEdit={() => startEdit(rowId, col.k)}
              onChange={v => handleCellChange(rowId, col, v)}
              onBlur={() => handleCellBlur(rowId, col, isVirtual, false, rd)}
              onKeyDown={e => handleKeyDown(e, rowId, colIdx)}
            />
          );
        })}
        <td className="border border-slate-200 px-1 py-1 text-center sticky right-0 bg-white" style={{minWidth:44}}>
          {isVirtual ? (
            <span className="text-[10px] text-slate-300">—</span>
          ) : (
            <button
              onClick={() => {
                if (isDateTab) {
                  if (!window.confirm('Clear all data in this row? The empty slot will remain.')) return;
                  // Clear content but keep date field
                  const clearedData = { ...row.row_data };
                  cols.forEach(c => { if (c.k !== 'date') clearedData[c.k] = ''; });
                  onSaveRow(row.id, clearedData);
                } else {
                  onDeleteRow(row.id);
                }
              }}
              className="p-1 rounded bg-red-400 text-white hover:bg-red-500" title={isDateTab ? 'Clear row data' : 'Delete row'}>
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </td>
      </tr>
    );
  };

  const renderBlankRow = (slotId) => {
    const rd = blankBase();
    return (
      <tr key={slotId} style={{background: localEdits[slotId] ? '#FFF9E6' : '#FBFCFE'}}>
        {/* Sr. No. placeholder for blank rows */}
        <td className="border border-slate-200 px-1 py-1 text-center text-[10px] text-slate-200 bg-slate-50/30 select-none" style={{minWidth:36,maxWidth:36}}>+</td>
        {cols.map((col, colIdx) => {
          const val = localEdits[slotId]?.[col.k] ?? getCellVal(rd, col);
          const isEditing = editingCell.rowId === slotId && editingCell.colKey === col.k;
          return (
            <SpreadsheetCell key={col.k} col={col} value={val} isEditing={isEditing} isFirst={colIdx===0}
              onStartEdit={() => startEdit(slotId, col.k)}
              onChange={v => handleCellChange(slotId, col, v)}
              onBlur={() => handleCellBlur(slotId, col, false, true, null)}
              onKeyDown={e => handleKeyDown(e, slotId, colIdx)} />
          );
        })}
        <td className="border border-slate-200 px-1 py-1 text-center sticky right-0 bg-white" style={{minWidth:44}}>
          <span className="text-[10px] text-slate-300">—</span>
        </td>
      </tr>
    );
  };

  const renderBody = () => {
    // HR date tabs: group by date with headers
    if (isHR && isDateTab) {
      const rows = [];
      let lastDate = null;
      let srCounter = 0;
      allDisplayRows.forEach((row) => {
        if (row._isSeparator) return;
        const dateISO = parseDateToISO(row.row_data?.date||'') || row.row_data?.date || '';
        if (dateISO !== lastDate) {
          lastDate = dateISO;
          const parts = dateISO.split('-');
          const dateLabel = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : (dateISO || '—');
          const cnt = allDisplayRows.filter(r => !r._isSeparator && (parseDateToISO(r.row_data?.date||'')||r.row_data?.date||'') === dateISO).length;
          rows.push(
            <tr key={`dhdr-${dateISO}`}>
              <td colSpan={cols.length + 2} style={{padding:'5px 12px', fontWeight:700, fontSize:11, color:'#1e40af', background:'#EFF6FF', borderBottom:'1px solid #BFDBFE', borderTop:'2px solid #93C5FD'}}>
                {dateLabel} &nbsp;&middot;&nbsp; <span style={{fontWeight:400,color:'#64748b'}}>{cnt} {cnt===1?'entry':'entries'}</span>
              </td>
            </tr>
          );
        }
        if (!row._isVirtual) srCounter++;
        rows.push(renderDataRow(row, srCounter));
      });
      return rows;
    }

    // Normal rendering
    const rows = [];
    let srCounter = 0;
    allDisplayRows.forEach(row => {
      if (row._isSeparator) {
        rows.push(
          <tr key="other-sep">
            <td colSpan={cols.length + 2} style={{padding:'4px 12px', background:'#F1F5F9', color:'#64748b', fontSize:10, fontWeight:600, borderTop:'2px dashed #CBD5E1'}}>
              DATA FROM OTHER MONTHS
            </td>
          </tr>
        );
        return;
      }
      if (!row._isVirtual) srCounter++;
      rows.push(renderDataRow(row, srCounter));
    });
    if (!isDateTab) blankIds.forEach(id => rows.push(renderBlankRow(id)));
    return rows;
  };

  return (
    <div className="flex flex-col gap-1">
      {/* Month navigation for date tabs (non-HR) */}
      {isDateTab && !isHR && (
        <div className="flex items-center gap-2 px-1">
          <button onClick={prevMonth} className="p-1 rounded hover:bg-slate-100 text-slate-600" data-testid="month-prev-btn">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs font-semibold text-slate-700 min-w-[120px] text-center">{monthLabel}</span>
          <button onClick={nextMonth} className="p-1 rounded hover:bg-slate-100 text-slate-600" data-testid="month-next-btn">
            <ChevronRight className="w-4 h-4" />
          </button>
          <span className="text-[11px] text-slate-400 ml-1">
            {getDatesInMonth(currentMonth).length} days &mdash; full month view &middot; {inMonthCount} {inMonthCount === 1 ? 'entry' : 'entries'} this month{otherMonthCount > 0 ? ` · ${otherMonthCount} from other months listed below (use the arrows to change month)` : ''}
          </span>
        </div>
      )}

      {/* Top scrollbar */}
      <div
        ref={topScrollRef}
        onScroll={handleTopScroll}
        style={{ overflowX: 'auto', overflowY: 'hidden', height: 16, borderRadius: 4 }}
        data-testid="top-scrollbar"
      >
        <div style={{ width: topScrollWidth || '100%', height: 1 }} />
      </div>

      <div
        ref={tableContainerRef}
        onScroll={handleTableScroll}
        className="overflow-auto border border-slate-200 rounded-lg"
        style={{maxHeight: 560}}
      >
        <table className="border-collapse text-xs" style={{width:'max-content',minWidth:'100%'}}>
          <thead style={{position:'sticky',top:0,zIndex:30}}>
            <tr>
              {/* Sr. No. column header */}
              <th style={{background:hdrColor,color:hdrFg,minWidth:36,maxWidth:36,padding:'6px 4px',fontWeight:700,fontSize:10,textAlign:'center',border:'1px solid rgba(0,0,0,0.15)',whiteSpace:'nowrap'}}>#</th>
              {cols.map((col,i) => (
                <th key={col.k} style={{
                  background:hdrColor, color:hdrFg,
                  minWidth: (col.k === 'no' && isIndexTab) ? 130 : col.w,
                  maxWidth: (col.k === 'no' && isIndexTab) ? 160 : col.w * 2,
                  padding: (col.k === 'no' && isIndexTab) ? '4px 6px' : '6px 8px',
                  fontWeight:700, fontSize:10, textAlign:'center',
                  border:'1px solid rgba(0,0,0,0.15)', whiteSpace:'nowrap',
                  position: i===0?'sticky':'static', left:0, zIndex:i===0?40:30,
                }}>
                  {col.k === 'no' && isIndexTab ? (
                    /* Inline NO selector inside the NO column header */
                    <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                      <span style={{fontSize:9,opacity:0.75,letterSpacing:1}}>NO</span>
                      <div style={{display:'flex',alignItems:'center',gap:3}}>
                        <select
                          value={bulkNo}
                          onChange={e => setBulkNo(e.target.value)}
                          data-testid="bulk-no-select"
                          style={{
                            fontSize:10, padding:'2px 3px',
                            background:'rgba(255,255,255,0.18)', color:'inherit',
                            border:'1px solid rgba(255,255,255,0.45)', borderRadius:3,
                            cursor:'pointer', outline:'none', width:52
                          }}
                        >
                          <option value="" style={{background:'#334155'}}>— NO —</option>
                          {NO_OPTS.filter(o => o !== '').map(o => (
                            <option key={o} value={o} style={{background:'#334155'}}>{o}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => applyBulkNo(bulkNo)}
                          disabled={!bulkNo || applyingBulkNo}
                          data-testid="bulk-no-apply-btn"
                          title="Apply selected NO to ALL rows"
                          style={{
                            fontSize:9, padding:'2px 6px', fontWeight:700,
                            background: bulkNo ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.08)',
                            color: 'inherit',
                            border:'1px solid rgba(255,255,255,0.4)', borderRadius:3,
                            cursor: bulkNo ? 'pointer' : 'not-allowed',
                            opacity: bulkNo ? 1 : 0.45,
                            whiteSpace:'nowrap'
                          }}
                        >
                          {applyingBulkNo ? '...' : '✓ All'}
                        </button>
                      </div>
                    </div>
                  ) : col.l}
                </th>
              ))}
              <th style={{background:hdrColor,color:hdrFg,padding:'6px 8px',fontSize:10,fontWeight:700,position:'sticky',right:0,zIndex:35,minWidth:44}}>DEL</th>
            </tr>
            {entries.length > 0 && (
              <tr style={{background:'#334155',color:'#fff',fontSize:10,fontWeight:600}}>
                <td style={{background:'#334155',minWidth:36}}></td>
                <td style={{padding:'4px 8px',position:'sticky',left:0,zIndex:25,background:'#334155',whiteSpace:'nowrap'}}>GRAND TOTAL</td>
                <td colSpan={cols.length - 1} style={{padding:'4px 8px',color:'#94a3b8'}}>{totalRow()}</td>
                <td style={{background:'#334155',position:'sticky',right:0,zIndex:25,minWidth:44}}></td>
              </tr>
            )}
          </thead>
          <tbody>
            {renderBody()}
          </tbody>
        </table>
      </div>
      <div className="mt-1 text-[11px] text-slate-400 italic" data-testid="report-table-hint">
        {isDateTab && !isHR ? `Showing ${monthLabel} — virtual rows (light blue) are empty; click to fill` : 'Click any cell to edit. Empty rows below auto-save when filled.'}
      </div>
    </div>
  );
};

export default SpreadsheetTable;
